import { APIProvider } from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useRef } from 'react';
import { Provider, useSelector } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import styled from 'styled-components';
import { useAppDispatch } from '@/store/store';
import { getLocation } from '@/store/slices/locationSlice';
import { selectIsLoggedIn, selectCurrentUser, setUser } from '@/store/slices/authSlice';
import { queryClient } from '@/lib/queryClient';
import { createAppRouter } from '@/router/router';
import { AblyProvider } from '@/lib/ably';
import { useGetSubscription } from '@/api/queries/useSubscriptionQueries';
import { useUIMetadata } from '@/api/queries/useConfigQueries';
import { useGetCurrentUser } from '@/api/queries/useUserQueries';
import { useDeepLinkHandler } from '@/hooks/useDeepLinkHandler';
import { useCheckoutForegroundRefresh } from '@/hooks/useCheckoutForegroundRefresh';
import { registerForPush } from '@/services/pushRegistration';
import { initAnalytics, analytics, identifyUser, waitForAnalytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { initSentry } from '@/lib/sentryInit';

/* Theme variables */
import '@/theme/variables.css';
import store from '@/store/store';

initSentry();

const GlobalStyleDiv = styled.div`
    font-family: MuseoModerno;
`;

// Component to prefetch subscription data when user is authenticated
function SubscriptionPrefetch() {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  // Prefetch subscription data when logged in
  // This ensures subscription data is available for feature gating throughout the app
  useGetSubscription({ enabled: isLoggedIn });

  return null;
}

// Component to prefetch UI metadata on app load
function UIMetadataPrefetch() {
  // Prefetch UI metadata on app load - always enabled since it's public data
  // This ensures toggle options, vibes, and other UI configuration is readily available
  useUIMetadata();

  return null;
}

// Syncs the Redux auth user with fresh data from the API on startup.
// Ensures name and photoUrl in localStorage never go stale between sessions.
function AuthUserSync() {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const dispatch = useAppDispatch();
  const { data: apiUser } = useGetCurrentUser({ enabled: isLoggedIn });

  useEffect(() => {
    if (!apiUser) return;
    dispatch(setUser({
      id: apiUser.id,
      name: apiUser.name,
      firstName: apiUser.firstName ?? '',
      lastName: apiUser.lastName ?? '',
      email: apiUser.email,
      photoUrl: apiUser.photoUrl ?? null,
    }));
  }, [apiUser, dispatch]);

  return null;
}

// Inner component that provides router with auth context
function RouterWithAuth() {
  const isAuthenticated = useSelector(selectIsLoggedIn);
  const router = useMemo(() => createAppRouter(queryClient), []);

  return (
    <RouterProvider
      router={router}
      context={{ queryClient, isAuthenticated }}
    />
  );
}

// Inner component that handles geolocation initialization and deep links
const AppWithGeolocation = () => {
  const dispatch = useAppDispatch();
  const user = useSelector(selectCurrentUser);
  const userRef = useRef(user);
  userRef.current = user;

  // Handle deep links from Stripe checkout redirects and other sources
  useDeepLinkHandler();
  // Native: refresh entitlements/issues once when the app returns from Stripe (D51f Phase 5).
  useCheckoutForegroundRefresh();

  useEffect(() => {
    // Initialize analytics (PostHog) once when the app starts
    initAnalytics();

    // Track app opened
    analytics.capture(AnalyticsEvents.APP_OPENED, {
      source: Capacitor.isNativePlatform() ? 'native' : 'web',
    });

    // Initialize geolocation once when the app starts
    dispatch(getLocation());

    // Set up app state change listeners for native platforms
    if (Capacitor.isNativePlatform()) {
      const stateChangeListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          analytics.capture(AnalyticsEvents.APP_FOREGROUNDED);
          // Re-identify on foreground in case native SDK lost identity
          const currentUser = userRef.current;
          if (currentUser?.id != null) {
            identifyUser(currentUser);
          }
        } else {
          analytics.capture(AnalyticsEvents.APP_BACKGROUNDED);
        }
      });

      return () => {
        stateChangeListener.then(listener => listener.remove());
      };
    }
  }, [dispatch]);

  // Re-register for push on every app launch (not just onboarding) so a token
  // rotated or dropped between sessions gets re-submitted to the server.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || user?.id == null) return;

    registerForPush().catch((error) => {
      console.error('[App] Failed to register for push on bootstrap:', error);
    });
  }, [user?.id]);

  // Re-identify user on startup when hydrated from localStorage.
  // Awaits the init promise instead of polling, and cancels on unmount.
  useEffect(() => {
    if (user?.id == null) return;

    let cancelled = false;

    const reidentify = async () => {
      await waitForAnalytics();
      if (cancelled || !analytics.isReady) return;
      identifyUser(user);
    };

    reidentify();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <AblyProvider>
      <APIProvider apiKey={import.meta.env.VITE_GOOGLE_API_KEY}>
        <SubscriptionPrefetch />
        <UIMetadataPrefetch />
        <AuthUserSync />
        <RouterWithAuth />
      </APIProvider>
    </AblyProvider>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AppWithGeolocation />
        {import.meta.env.DEV && !import.meta.env.VITE_HIDE_DEVTOOLS  && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
